// PdfPages — render a PDF's pages onto canvases via pdfjs-dist.
//
// The one deliberately effectful leaf of SourceView: rasterising pages is
// inherently canvas I/O, so it is fenced into this component's effects (the
// same containment discipline as theme.tsx's DOM writes) and the rest of the
// view stays pure. The bytes arrive as a prop; no fetching happens here.
//
// Two state machines, deliberately kept apart:
//  - `PdfRenderState` (the document): "counting" until pdfjs resolves a doc,
//    then "rendering" the moment `doc.numPages` is known — BEFORE any page
//    is rasterized — then "done" once every page has painted. The count is
//    reported up (`onPageCount`) as soon as it's known, not after a full
//    raster pass, so the enclosing rail never waits on rasterization.
//  - `PagePaintState` (one page, in `PdfPage` below): each page starts as a
//    skeleton sized to the document's page aspect ratio (no reflow when the
//    real canvas swaps in) and paints only once its slot nears the viewport
//    (`IntersectionObserver`) — long PDFs don't pay for pages nobody scrolled
//    to. MVP shortcut: a page that scrolls out of view keeps its canvas
//    (never torn down); only the FIRST paint is deferred. Revisit if long
//    PDFs show memory pressure.
//
// Render strategy: fit page width to its slot, backing-store scaled by
// devicePixelRatio for sharpness. A per-page cancellation flag + `destroy()`
// on the document stop a stale document/page from painting over a newer one.
// Failure is a typed presentation state rendered as an honest sentence —
// never a blank surface.

import { Plural, Trans, useLingui } from "@lingui/react/macro";
// Loaded lazily inside the effect so the (heavy) pdfjs module and its
// worker never load for authors who open no PDF.
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import type { PageSpan } from "../Navigation/types.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { Text } from "../Text/Text.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { pickCurrentPage } from "./pageNav.js";
import {
    itemRect,
    type PageRect,
    type PdfTextItem,
    quoteItems,
} from "./pdfQuote.js";

// A referenced passage to show on its page (#89): its number (the one its
// card in "Used in" / "Facts" carries), the exact words, and the page.
export type PdfMarker = {
    readonly n: number;
    readonly quote: string;
    readonly page: number;
};

export type PdfPagesProps = {
    readonly data: Uint8Array;
    // A stable prefix for each page's DOM id (`${idPrefix}-page-N`), so the
    // Paginated renderer's page rail can scroll a page into view. Absent ⇒
    // pages carry no id (the rail simply isn't wired).
    readonly idPrefix?: string;
    // Pages to mark visibly (a span a chat tool read), so the author sees
    // which pages the reference covers. Absent ⇒ no page is marked.
    readonly markedPages?: PageSpan;
    // Reports the page count up as soon as the document loads, before any
    // page rasterizes, so the enclosing renderer can reserve a rail of that
    // many page buttons on the first paint that knows the count. Never
    // called on a load failure (the count is unknown then).
    readonly onPageCount?: (pageCount: number) => void;
    // Reports the page most visible in the viewport as the author scrolls
    // (view-local DOM state, never domain state).
    readonly onCurrentPage?: (page: number) => void;
    // Referenced passages: highlighted where their words are on the page,
    // else (a scanned page) named beside the page. The selected one is
    // emphasised; a click on a marker reports its number.
    readonly markers?: readonly PdfMarker[];
    readonly selectedMarker?: number;
    readonly onMarker?: (n: number) => void;
};

const NO_MARKERS: readonly PdfMarker[] = [];

// The document's own state — distinct from any one page's paint state (see
// `PagePaintState`). `pdf_done` means every page has painted, which with lazy
// rendering may take a while (or never, if the author never scrolls) — it
// exists for the completion announcement below, not to gate anything.
type PdfRenderState =
    | { readonly type: "pdf_counting" } // doc not loaded yet
    | { readonly type: "pdf_rendering"; readonly pageCount: number } // count known, pages paint in lazily
    | { readonly type: "pdf_done"; readonly pageCount: number }
    | { readonly type: "pdf_failed" };

// A page before its aspect ratio is known falls back to a plausible
// portrait ratio so the very first skeleton (page 1's own, before its own
// viewport resolves) still reserves roughly the right shape.
const FALLBACK_PAGE_ASPECT_RATIO = "3 / 4";

type PagePaintState =
    | { readonly type: "page_pending" } // waiting to near the viewport
    | { readonly type: "page_painted" }
    // Rare (the document already opened successfully) — the skeleton stays
    // in place rather than a new error string; documented MVP shortcut,
    // not a new failure surface.
    | { readonly type: "page_failed" };

type PdfPageProps = {
    readonly doc: PDFDocumentProxy;
    readonly pageNumber: number;
    readonly idPrefix: string | undefined;
    // This page lies in the marked span.
    readonly marked: boolean;
    // CSS `aspect-ratio` value (`"width / height"`), shared by every page in
    // the document — reserves the page's final footprint before it paints.
    readonly aspectRatio: string;
    readonly onPainted: () => void;
    // This page's visible fraction (0..1) each time it changes.
    readonly onVisibleRatio: (page: number, ratio: number) => void;
    // The markers on this page, the selected one, and the click.
    readonly markers: readonly PdfMarker[];
    readonly selectedMarker: number | undefined;
    readonly onMarker: ((n: number) => void) | undefined;
};

// Where each marker's words are on the page (empty: not found there).
type Located = ReadonlyMap<number, readonly PageRect[]>;

// One page: a skeleton at the page's final aspect ratio, swapped for a
// rasterized canvas once the slot nears the viewport. The label sits above
// the ratio-boxed slot so it never itself shifts (it's a fixed one-line
// height, painted from the very first frame this page exists in).
const PdfPage = ({
    doc,
    pageNumber,
    idPrefix,
    marked,
    aspectRatio,
    onPainted,
    onVisibleRatio,
    markers,
    selectedMarker,
    onMarker,
}: PdfPageProps) => {
    const { t } = useLingui();
    const slotRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const pageRef = useRef<PDFPageProxy | null>(null);
    const [state, setState] = useState<PagePaintState>({
        type: "page_pending",
    });
    // View-local, read off the painted page's own text: where each marker's
    // words are. Recomputed when the markers change.
    const [located, setLocated] = useState<Located>(new Map());

    // biome-ignore lint/correctness/useExhaustiveDependencies: `doc`/`pageNumber` are fixed for this component's whole lifetime (a fresh `PdfPage` mounts per document via `key` in the parent's page list), so there is nothing to re-subscribe to.
    useEffect(() => {
        const slot = slotRef.current;
        const canvas = canvasRef.current;
        if (slot === null || canvas === null) return;
        let cancelled = false;
        const paint = async () => {
            try {
                const page = await doc.getPage(pageNumber);
                if (cancelled) return;
                const width = slot.clientWidth || 640;
                const ratio = window.devicePixelRatio || 1;
                const base = page.getViewport({ scale: 1 });
                const viewport = page.getViewport({
                    scale: (width / base.width) * ratio,
                });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const canvasContext = canvas.getContext("2d");
                if (canvasContext === null) {
                    throw new Error("2d canvas context unavailable");
                }
                await page.render({ canvasContext, viewport }).promise;
                if (cancelled) return;
                pageRef.current = page;
                setState({ type: "page_painted" });
                onPainted();
            } catch {
                if (!cancelled) setState({ type: "page_failed" });
            }
        };
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                observer.disconnect();
                void paint();
            },
            { rootMargin: "50% 0px" }, // start painting just before the page is actually visible
        );
        observer.observe(slot);
        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, []);

    // Locate the markers' words in the painted page's text layer. A scanned
    // page has no text, so every marker stays unlocated (named beside it).
    const markersKey = markers.map((m) => `${m.n}:${m.quote}`).join("\n");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `markersKey` stands for `markers` (a fresh array each render).
    useEffect(() => {
        const page = pageRef.current;
        if (state.type !== "page_painted" || page === null) return;
        if (markers.length === 0) {
            setLocated(new Map());
            return;
        }
        let cancelled = false;
        const locate = async () => {
            try {
                const base = page.getViewport({ scale: 1 });
                const content = await page.getTextContent();
                if (cancelled) return;
                // The text runs (marked-content entries carry no text).
                const items: readonly PdfTextItem[] = content.items.flatMap(
                    (i) => ("str" in i ? [i] : []),
                );
                const strs = items.map((i) => i.str);
                setLocated(
                    new Map(
                        markers.map((m) => [
                            m.n,
                            quoteItems(strs, m.quote).flatMap((i) => {
                                const item = items[i];
                                return item === undefined
                                    ? []
                                    : [itemRect(item, base)];
                            }),
                        ]),
                    ),
                );
            } catch {
                if (!cancelled) setLocated(new Map());
            }
        };
        void locate();
        return () => {
            cancelled = true;
        };
    }, [state.type, markersKey]);

    // Scroll tracking: a separate observer from the lazy-paint one above (that
    // disconnects after the first paint; this lives as long as the page).
    // biome-ignore lint/correctness/useExhaustiveDependencies: `pageNumber`/`onVisibleRatio` are fixed for this component's lifetime (see the paint effect above).
    useEffect(() => {
        const slot = slotRef.current;
        if (slot === null) return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    onVisibleRatio(
                        pageNumber,
                        entry.isIntersecting ? entry.intersectionRatio : 0,
                    );
                }
            },
            { threshold: [0, 0.25, 0.5, 0.75, 1] },
        );
        observer.observe(slot);
        return () => observer.disconnect();
    }, []);

    const unlocated = markers.filter(
        (m) => (located.get(m.n) ?? []).length === 0,
    );
    const markerButton = (m: PdfMarker, style?: CSSProperties) => (
        <button
            key={m.n}
            type="button"
            className="bilda-pdf-marker"
            data-selected={m.n === selectedMarker ? "true" : undefined}
            style={style}
            aria-label={t`Reference ${m.n}`}
            // The pages are hidden from assistive tech (the extracted text is
            // the readable path); the same references are the cards beside.
            tabIndex={-1}
            onClick={() => onMarker?.(m.n)}
        >
            {m.n}
        </button>
    );

    return (
        <div
            id={
                idPrefix === undefined
                    ? undefined
                    : `${idPrefix}-page-${pageNumber}`
            }
            className="bilda-source-view__pdf-page-slot"
            data-marked={marked ? "true" : undefined}
        >
            {/* Passages whose words aren't on the page image (a scanned
                page): named beside the page, so the reference is still seen. */}
            {state.type === "page_painted" && unlocated.length > 0 ? (
                <div className="bilda-pdf-page-markers">
                    <Text as="span" size="xs" tone="dim">
                        {t`Referenced on this page`}
                    </Text>
                    {unlocated.map((m) => markerButton(m))}
                </div>
            ) : null}
            {/* The page control above already names the current page, so a
                plain per-page "Page N" label would only repeat it; a page
                earns a label only when a reference marks it. */}
            {marked ? (
                <Text
                    size="xs"
                    as="p"
                    className="bilda-source-view__pdf-page-label"
                >
                    {t`Page ${pageNumber} · referenced`}
                </Text>
            ) : null}
            <div
                ref={slotRef}
                className="bilda-source-view__pdf-page-frame"
                style={{ aspectRatio }}
            >
                {state.type !== "page_painted" ? (
                    <Skeleton
                        variant="block"
                        width="100%"
                        height="100%"
                        radius="sm"
                    />
                ) : null}
                <canvas
                    ref={canvasRef}
                    className="bilda-source-view__pdf-page"
                    style={{
                        display:
                            state.type === "page_painted" ? "block" : "none",
                    }}
                />
                {state.type === "page_painted"
                    ? markers.flatMap((m, index) => {
                          const rects = located.get(m.n) ?? [];
                          const first = rects[0];
                          if (first === undefined) return [];
                          // Markers whose passages start on the same line sit
                          // side by side, never on top of each other.
                          const beside = markers.slice(0, index).filter((o) => {
                              const top = located.get(o.n)?.[0]?.top;
                              return (
                                  top !== undefined &&
                                  Math.abs(top - first.top) < first.height
                              );
                          }).length;
                          return [
                              ...rects.map((r, i) => (
                                  <span
                                      // biome-ignore lint/suspicious/noArrayIndexKey: a marker's rects are fixed for its words
                                      key={`${m.n}-${i}`}
                                      className="bilda-pdf-highlight"
                                      data-selected={
                                          m.n === selectedMarker
                                              ? "true"
                                              : undefined
                                      }
                                      style={{
                                          left: `${r.left * 100}%`,
                                          top: `${r.top * 100}%`,
                                          width: `${r.width * 100}%`,
                                          height: `${r.height * 100}%`,
                                      }}
                                  />
                              )),
                              markerButton(m, {
                                  top: `${first.top * 100}%`,
                                  left: `calc(var(--space-1) + ${beside} * (var(--space-5) + var(--space-1)))`,
                              }),
                          ];
                      })
                    : null}
            </div>
        </div>
    );
};

export const PdfPages = ({
    data,
    idPrefix,
    markedPages,
    onPageCount,
    onCurrentPage,
    markers = NO_MARKERS,
    selectedMarker,
    onMarker,
}: PdfPagesProps) => {
    const [state, setState] = useState<PdfRenderState>({
        type: "pdf_counting",
    });
    const [aspectRatio, setAspectRatio] = useState<string>(
        FALLBACK_PAGE_ASPECT_RATIO,
    );
    const docRef = useRef<PDFDocumentProxy | null>(null);
    const renderedCountRef = useRef(0);
    // Latest-callback ref so a new `onPageCount` identity from the parent
    // never re-runs the (heavy) document-load effect below — it keys on
    // `data` alone.
    const onPageCountRef = useRef(onPageCount);
    useEffect(() => {
        onPageCountRef.current = onPageCount;
    }, [onPageCount]);

    useEffect(() => {
        // A fresh `data` means a different document — start over rather than
        // showing the previous document's pages/state while the new one loads.
        setState({ type: "pdf_counting" });
        setAspectRatio(FALLBACK_PAGE_ASPECT_RATIO);
        let cancelled = false;
        let destroy = (): void => {};
        const run = async () => {
            try {
                const pdfjs = await import("pdfjs-dist");
                pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
                // Copy the bytes: pdfjs transfers the buffer to its worker
                // (detaching it), and the prop must survive a re-render.
                const task = pdfjs.getDocument({ data: data.slice() });
                const doc = await task.promise;
                destroy = () => {
                    void doc.destroy();
                };
                if (cancelled) return;
                docRef.current = doc;
                renderedCountRef.current = 0;
                setState({ type: "pdf_rendering", pageCount: doc.numPages });
                onPageCountRef.current?.(doc.numPages);
                // Page 1's viewport is cheap (one page's metadata, not a
                // render) and every page in a PDF shares its aspect ratio —
                // enough to size every OTHER page's skeleton with no reflow
                // once its own canvas lands.
                const page1 = await doc.getPage(1);
                if (cancelled) return;
                const viewport = page1.getViewport({ scale: 1 });
                setAspectRatio(`${viewport.width} / ${viewport.height}`);
            } catch {
                if (!cancelled) setState({ type: "pdf_failed" });
            }
        };
        void run();
        return () => {
            cancelled = true;
            destroy();
            docRef.current = null;
        };
    }, [data]);

    const handlePagePainted = (): void => {
        renderedCountRef.current += 1;
        setState((prev) =>
            prev.type === "pdf_rendering" &&
            renderedCountRef.current >= prev.pageCount
                ? { type: "pdf_done", pageCount: prev.pageCount }
                : prev,
        );
    };

    const ratiosRef = useRef(new Map<number, number>());
    const handleVisibleRatio = (page: number, ratio: number): void => {
        ratiosRef.current.set(page, ratio);
        const current = pickCurrentPage(ratiosRef.current);
        if (current !== null) onCurrentPage?.(current);
    };

    const doc = docRef.current;
    const pageCount =
        state.type === "pdf_rendering" || state.type === "pdf_done"
            ? state.pageCount
            : null;

    return (
        <div className="bilda-source-view__pdf">
            {state.type === "pdf_failed" ? (
                <Text size="sm" tone="dim" as="p">
                    <Trans>
                        The PDF pages couldn't be rendered. The extracted text
                        is still available.
                    </Trans>
                </Text>
            ) : null}
            {pageCount !== null ? (
                <VisuallyHidden>
                    <Plural
                        value={pageCount}
                        one="PDF loaded, # page."
                        other="PDF loaded, # pages."
                    />
                </VisuallyHidden>
            ) : null}
            {/* The pages are a visual rendering of the same content the
                extracted-text presentation carries — hidden from AT so the
                readable path is the text, not an unlabeled image run. */}
            {doc !== null && pageCount !== null ? (
                <div
                    className="bilda-source-view__pdf-pages"
                    aria-hidden="true"
                >
                    {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                        (pageNumber) => (
                            <PdfPage
                                key={pageNumber}
                                doc={doc}
                                pageNumber={pageNumber}
                                idPrefix={idPrefix}
                                marked={
                                    markedPages !== undefined &&
                                    pageNumber >= markedPages.first &&
                                    pageNumber <= markedPages.last
                                }
                                aspectRatio={aspectRatio}
                                onPainted={handlePagePainted}
                                onVisibleRatio={handleVisibleRatio}
                                markers={markers.filter(
                                    (m) => m.page === pageNumber,
                                )}
                                selectedMarker={selectedMarker}
                                onMarker={onMarker}
                            />
                        ),
                    )}
                </div>
            ) : null}
            {/* Before the document loads, the page count/shape are unknown —
                still reserve a page-shaped footprint (fallback aspect ratio)
                rather than leaving the pane empty while it counts. */}
            {state.type === "pdf_counting" ? (
                <div
                    className="bilda-source-view__pdf-pages"
                    aria-hidden="true"
                >
                    <div
                        className="bilda-source-view__pdf-page-frame"
                        style={{ aspectRatio: FALLBACK_PAGE_ASPECT_RATIO }}
                    >
                        <Skeleton
                            variant="block"
                            width="100%"
                            height="100%"
                            className="bilda-source-view__pdf-page-skeleton"
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
